import { STAGE_OPTIONS, STATUS_OPTIONS } from "./constants";

/*
 * Column definitions for the Preview grid — the visible column set of the legacy where-builder grid.
 *
 * Which columns those are is decided by the Abecas `VisibleOnGrid` property in uWMM0077.dfm, NOT by
 * standard `TField.Visible`: TAbWhereBuilder.Preview clones each field and copies VisibleOnGrid onto
 * the clone but never copies Visible, so the clone's Visible is always the default True. Consequence:
 * KEYSEQUENCE (TAbIntegerField, whose VisibleOnGrid defaults True) IS a preview column, and ROWID
 * (TAbBytesField, whose VisibleOnGrid defaults False) is not.
 *
 * Order follows the DFM's persistent-field declaration order, which is the order Delphi laid the grid
 * out, except Record # is lifted to the front (Architecture.md rule 10 and this app's convention).
 *
 * dataField casing matches RecordsReadyToCommitDto's property names exactly, because the endpoint
 * serialises with DefaultContractResolver, i.e. PascalCase straight off the DTO.
 *
 * dataType is declared on EVERY column, deliberately. DevExtreme only infers it from the first non-null
 * value, and several of these are routinely empty for a whole result set (Truck Reference and the
 * consignee code on receipts, which have no consignee at all). With nothing to infer from, the column
 * gets no filter-row editor and the user silently loses the ability to filter it.
 *
 * minWidth on every column is what keeps the grid from squeezing 14 columns into the viewport: with
 * columnAutoWidth the total then exceeds the container and DevExtreme shows a horizontal scrollbar
 * instead of compressing everything to unreadable slivers.
 */

// Unpadded, matching the Delphi grid: AI8 renders "5/22/2013", not "05/22/2013". Both date fields here
// are TAbDateTimeField with TimeStorageFormat/DisplayFormat tfLocal, i.e. timestamps — the same reading
// the sibling WMD0012 preview takes for its transaction date.
const DATETIME_FORMAT = "M/d/yyyy h:mm:ss a";

const TEXT = "string";
const DATETIME = "datetime";

// Widths tuned to the caption plus a plausible value, so nothing is truncated at the default zoom.
const W_CODE = 120;
const W_NAME = 180;
const W_DATETIME = 165;
const W_SHORT = 90;

export const PREVIEW_KEY_EXPR = "RowId";

const text = (dataField, caption, width = W_CODE) => ({
    dataField,
    caption,
    dataType: TEXT,
    minWidth: width,
});

const dateTime = (dataField, caption) => ({
    dataField,
    caption,
    dataType: DATETIME,
    format: DATETIME_FORMAT,
    minWidth: W_DATETIME,
});

/**
 * A coded column rendered as its description (Architecture.md rule 3). The option lists cover every
 * value this query can return — a value outside the lookup renders as an EMPTY cell, not as the raw
 * code, so a short list is worse than no lookup at all.
 */
const coded = (dataField, caption, options, width = W_NAME) => ({
    ...text(dataField, caption, width),
    lookup: { dataSource: options, displayExpr: "text", valueExpr: "id" },
});

export const PREVIEW_COLUMNS = [
    // The key. Hidden and kept out of the column chooser (Architecture.md rule 8) — users never see raw
    // ids, and AI8 did not show it either.
    { dataField: "RowId", dataType: TEXT, visible: false, showInColumnChooser: false },

    text("RecordNumber", "Record #", W_SHORT),
    text("TransactionNumber", "Transaction #"),
    dateTime("TransactionDate", "Transaction Date"),
    dateTime("ActualReceivedShippedDateTime", "Recv/Ship Date"),
    text("ShipperBolNumber", "Shipper #"),
    text("Reference", "PO/Reference", W_NAME),
    text("TruckReference", "Truck Reference", W_NAME),
    coded("Status", "Status", STATUS_OPTIONS),
    coded("Stage", "Stage", STAGE_OPTIONS, 230),
    text("ProfileCode", "Profile Code"),

    // The four identities the transfer requires. Customer comes through IdentityId — the DTO's original
    // customer field — while the other three are this screen's additions, aliased IDMST / IDMCO / IDMSV
    // server-side to match the procedure's own FROM list.
    text("ShipperId", "Shipper ID"),
    text("TransportCarrierId", "Transport Carrier ID", W_NAME),
    text("ConsigneeId", "Consignee ID"),
    text("IdentityId", "Customer ID"),
];
